import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'

import isEqual from 'lodash.isequal'

import { checkDotNet, getHost } from './utilities'

declare global {
  interface Window {
    sketchup?: any
    chrome?: any
  }
}

export interface MeshOptions {
  gridSize?: number
  union?: boolean
}

export interface GeometryFilter {
  type?: Array<string>
  layer?: Array<string>
}

interface PanelMessageOut {
  data: any
  identifier: any
}

export const useGetGeometry = (
  meshOptions: MeshOptions = { },
  geometryFilter: GeometryFilter = { } ) => {
  const host: string = getHost()

  const [geometry, setGeometry] = useState<Blob | undefined>(undefined)

  const geometryRef = useRef<Uint8Array | undefined>(undefined)

  const setReturnVal = useCallback(({ data }: PanelMessageOut, 
    force: boolean) => {
    const bytes = new TextEncoder().encode(data)

    if (!force && geometryRef.current) {
      if (!isEqual(bytes, geometryRef.current)) {
        setGeometry(
          new Blob([bytes], {
            type: 'application/json;charset=utf-8',
          }),
        )
      }
    } else {
      setGeometry(
        new Blob([bytes], {
          type: 'application/json;charset=utf-8',
        }),
      )
    }
    geometryRef.current = bytes
  }, [])

  useEffect(() => {

    // @ts-ignore
    window.parent.readGeometry = (message: PanelMessageOut) => {
      if (typeof message.data !== 'string'
         || typeof message.identifier !== 'string') throw new Error('readGeometry did not receive a valid string object.')

      setReturnVal(message, false)
    }

    // @ts-ignore
    return () => window.parent.readGeometry = undefined
  }, [host, setReturnVal, meshOptions, geometryFilter])

  const dotNetGetGeometry = useCallback((key = 'use-get-geometry', selection: boolean = false, 
    force: boolean = false, useSelection: boolean = false): void => {
    if (!checkDotNet()) return

    let res: PanelMessageOut = {
      data: null,
      identifier: null,
    }

    const optionalValues = { ...meshOptions, ...geometryFilter }

    const options = (Object.values(optionalValues).length !== 0) ? {
      select: selection,
      useSelection: useSelection,
      gridSize: optionalValues?.gridSize,
      merge: optionalValues?.union,
      layer: optionalValues?.layer,
      type: optionalValues?.type,
    } : {
      select: selection,
      useSelection: useSelection
    }

    res = window.parent.chrome.webview.hostObjects.sync.geometry.Read(
      key,
      JSON.stringify(options)
    )

    if (res) {
      setReturnVal(res, force)
    }
  }, [setReturnVal, meshOptions, geometryFilter])

  const getGeometry = useMemo(() => {
    switch (host) {
      case 'rhino':
        return dotNetGetGeometry
      case 'revit':
        return dotNetGetGeometry
      case 'sketchup':
        return dotNetGetGeometry
      default:
        return undefined
    }
  }, [dotNetGetGeometry, host])

  return {
    host,
    geometry,
    getGeometry,
  }
}
